<template>
  <div class="area-filter">
    <select v-bind:value="selectedPref" @change="onPrefSelected" class="uk-select" aria-label="Select Area">
      <option :value="''" key="0">{{ prefConfig.label }}</option>
      <option v-for="pref in prefs" :value="pref" :key="pref">{{ pref }}</option>
    </select>
    <select v-bind:value="cities.includes(selectedCity) ? selectedCity : ''" @change="onSelectedCity" class="uk-select" aria-label="Select City" :disabled="!selectedPref || !cities?.length">
      <option :value="''" key="0">{{ cityConfig.label }}</option>
      <option v-for="city in cities" :value="city" :key="city">{{ city }}</option>
    </select>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, PropType } from 'vue'
import store from '@/store'
import Page from '@/services/Page'
import PointsService from '@/services/PointsService.ts'
import { Filters } from '@/services/Filters.ts'

interface AreaConfig {
  /**
   * 選択された追加フィールドを識別するためのキー
   */
  key: string
  /**
   * セレクトボックスが選択されていない場合に表示するラベル
   */
  label: string
  /**
   * 絞り込みを判定するポイントの追加フィールドのキー
   */
  point_extra_field_key: string
}

const props = defineProps({
  prefConfig: {
    type: Object as PropType<AreaConfig>,
    required: true
  },
  cityConfig: {
    type: Object as PropType<AreaConfig>,
    required: true
  },
  pointExtraFieldKeyForSortCities: {
    type: String,
    default: '',
    required: false
  }
})

/**
 * 都道府県名とエリアコードのリスト
 * @type {Array} [{ areaName: 都道府県名, areaCode: エリアコード }, ...]
 */
const areas: Array<{ areaName: string, areaCode: string}> = Object.values(storelocator.area).flatMap(a => Object.values(a.areas).map(area => ({ areaName: area.area_name, areaCode: area.area_code })))

/**
 * 選択中の都道府県の名前の初期値
 * 都道府県選択後のリセットに使用するため、別の変数として保持
 */
const initialPref = areas.find(a => a.areaCode === storelocator.area_code)?.areaName || ''

/**
 * 選択中の都道府県の名前
 */
const selectedPref = ref<string>(initialPref)

/**
 * 選択中の市区郡の名前
 */
const selectedCity = computed(() => store.state.filters.selectedExtraFields?.city?.value || '')

/**
 * 都道府県の選択肢 [pref1, pref2, ...]
 */
const prefs = Object.values(storelocator.area).flatMap(a => Object.values(a.areas).map(area => area.area_name))

/**
 * 市区郡の選択肢
 * @returns {Array} [city1, city2, ...]
 */
const cities = ref<string[]>([])

/**
 * 都道府県選択
 */
const onPrefSelected = (event: Event) => {
  const pref = event.target.value

  /**
   * ブラウザバック対策
   * 都道府県の選択状態が残らないようにリセットする
   * vueに変更を検知させるために、一度prefをセットしてから初期値に戻す
   */
  selectedPref.value = pref
  selectedPref.value = initialPref

  /**
   * 都道府県のページに遷移、クエリパラメータを保持
   * 遷移前にstateを更新すると即時にポイントが更新されて画面がチラつくため、state更新はページ遷移後のonMountedで行う
   */

  const params = location.search.replace(/^\?/, '').split('&').filter(param => param !== '')
  window.location.href = Page.getPointsUrl(Number(areas.find(a => a.areaName === pref)?.areaCode), params )
}

/**
 * 市区郡選択時
 */
const onSelectedCity = (event: Event) => {
  store.dispatch("changeSelectedExtraField", { key: props.cityConfig.key, extraField: { key: props.cityConfig.point_extra_field_key, value: event.target.value } })
  Filters.cache.updateSelectedExtraFields()
}

onMounted(() => {
  if (selectedPref.value !== '') {
    cities.value = generateCities(PointsService.resultPoints.value)
  }

  // リロード時以外で市区郡が選択されている場合
  if (!(store.state.filters.selectedExtraFields?.pref?.value === selectedPref.value) && selectedCity.value) {
    // 選択中の市区郡をクリア
    store.dispatch("changeSelectedExtraField", { key: props.cityConfig.key, extraField: { key: props.cityConfig.point_extra_field_key, value: '' } })
    Filters.cache.updateSelectedExtraFields()
  }

  store.dispatch("changeSelectedExtraField", { key: props.prefConfig.key, extraField: { key: props.prefConfig.point_extra_field_key, value: selectedPref.value } })
  Filters.cache.updateSelectedExtraFields()
})

/**
 * 市区郡の選択肢を生成
 * @param points
 * @returns {Array} [city1, city2, ...]
 */
const generateCities = (points: Point[]) => {
  const cityList: string[] = []

  points.filter((p) => p.extra_fields[props.prefConfig.point_extra_field_key] === selectedPref.value)
    .sort((a, b) => {
      // 並び順は昇順　空文字は最後に
      const aValue = a.extra_fields[props.pointExtraFieldKeyForSortCities] || ''
      const bValue = b.extra_fields[props.pointExtraFieldKeyForSortCities] || ''
      if (aValue === '' && bValue !== '') return 1
      if (aValue !== '' && bValue === '') return -1
      return aValue > bValue ? 1 : -1
    })
    .forEach(p => {
      const city = p.extra_fields[props.cityConfig.point_extra_field_key]
      if (city) {
        cityList.push(city)
      }
    })

  return Array.from(new Set(cityList))
}
</script>

<style lang="scss" scoped>
.area-filter {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  width: 400px;
  
  @media screen and (max-width: $breakpoint-medium) {
    width: 100%;
  }
  .uk-select {
    border: 1px solid #ddd;
    border-radius: 20px;
    font-size: 0.875rem !important;
    padding-left: 16px;
  }
}
</style>
